<template>
  <div class="bulk-order-confirm__container">
    <div class="bulk-order-confirm__header-container mb-16">
      <PageHeadlineForBulkOrder
        title="お申し込み内容最終確認"
        :step="3"
        class="bulk-order-confirm__header-title"
      />
    </div>
    <form
      action="/bulk_order"
      method="post"
      accept-charset="UTF-8"
      @submit="onSubmit"
    >
      <input type="hidden" name="authenticity_token" :value="csrfToken" />
      <div class="bulk-order-confirm__contents-container mb-16">
        <div class="bulk-order-confirm__contents">
          <BulkOrderConfirmSection title="お申し込みいただくギフト">
            <BulkOrderConfirmGift
              v-for="(itemSku, index) in itemSkus"
              :key="index"
              class="bulk-order-confirm__gift"
              :brand-name="itemSku.brandName"
              :item-name="itemSku.itemName"
              :item-sku-name="itemSku.itemSkuName"
              :price="itemSku.price"
              :quantity="itemSku.quantity"
              :ticket-type="itemSku.ticketType"
              :is-including-tax="itemSku.isIncludingTax"
              :limited-end-date-time="itemSku.limitedEndDateTime"
            />
          </BulkOrderConfirmSection>
          <BulkOrderConfirmSection title="支払総額">
            <p class="minimal-black">
              {{ `¥${summaryPrice.toLocaleString()}`
              }}<span v-if="isSummaryPriceIncludingTax">（税込）</span>
            </p>
          </BulkOrderConfirmSection>
          <BulkOrderConfirmSection title="支払手段 / 支払時期">
            <p class="minimal-black">銀行振り込み</p>
          </BulkOrderConfirmSection>
          <BulkOrderConfirmSection title="キャンセル / 返品等について">
            <p class="minimal-black">
              お客様がお申し込み内容の確認画面で「申し込みを確定する」ボタンをクリックした時点で、仕様・注意事項に同意いただいたものとし、お客様のお申し込みが確定したものとさせていただきます。
            </p>
            <p class="minimal-black">
              <span>※</span>
              <strong>
                お申し込み完了時点では、ご希望商品のご購入は確約されておりません。
              </strong>
              <span>
                商品の在庫不足等によりご購入が難しい場合は、お申し込み内容についてご相談をさせていただく場合がございますので、あらかじめご了承ください。
              </span>
            </p>
            <br />
            <p class="minimal-black">
              「申し込みを確定する」ボタンのクリック後、メールにて請求書を送付いたしますので、請求書に記載された入金期限内に代金のお振込みをお願いいたします。ご入金確認後におけるお申し込み内容の変更・キャンセルは、以下の場合を除き承ることができませんので、あらかじめご了承ください。
            </p>
            <br />
            <p class="minimal-black">
              サービスの停止により、電子チケットと商品の交換が不能となった場合
            </p>
            <p class="minimal-black">
              システム障害により、電子チケットと商品の交換が不能になった場合
            </p>
            <p class="minimal-black">
              対象商品等の在庫不足、販売終了または対象店舗の閉鎖等により対象商品等との交換が不能と当社が判断した場合
            </p>
            <p class="minimal-black">
              <span>キャンセル、返品、ギフトに関するお問い合わせは、</span>
              <a
                href="/contact/top"
                class="bulk-order-confirm__link-text"
                target="_blank"
              >
                お問い合わせフォーム
              </a>
              <span>よりご連絡ください。</span>
            </p>
          </BulkOrderConfirmSection>
        </div>
      </div>
      <div class="bulk-order-confirm__action-container py-40 px-16">
        <div class="bulk-order-confirm__action">
          <div class="bulk-order-confirm__caption mb-40">
            <p class="minimal">
              ※本サービスの対応は平日のみとなっております。週末や祝日にいただいたお申し込みは、翌営業日（平日）に確認させていただきますのでご了承ください。
            </p>
            <p class="minimal">
              ※お申し込みいただいた内容に関して、メールでご連絡差し上げます。迷惑メールの対策、キャリアメール等、ドメイン指定を行っている場合、メールが受信できない場合がございますので「@giftee.com」を受信いただけるようにご設定をお願いいたします。
            </p>
          </div>
          <div class="bulk-order-confirm__button-container">
            <ButtonPrimary
              text="申し込みを確定する"
              type="submit"
              class="bulk-order-confirm__submit-button mb-24"
              :disabled="isSubmitting"
            />
            <TextLink type="button" href="/bulk_order/gift_cards">
              ギフトカードリストに戻る
            </TextLink>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import PageHeadlineForBulkOrder from '~/src/js/customer/components/organism/bulk_personal_orders/PageHeadlineForBulkOrder.vue'
import BulkOrderConfirmSection from '~/src/js/customer/components/pages/bulk_order/confirm/BulkOrderConfirmSection.vue'
import ButtonPrimary from '~/src/js/customer/components/molecules/buttons/ButtonPrimary.vue'
import TextLink from '~/src/js/customer/components/atoms/text_links/TextLink.vue'
import BulkOrderConfirmGift from '../confirm/BulkOrderConfirmGift.vue'
import { computed, ref } from 'vue'

const props = defineProps({
  itemSkuContents: {
    type: Array,
    required: true
  },
  summaryPrice: {
    type: Number,
    required: true
  }
})

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  .getAttribute('content')
const isSubmitting = ref(false)

const itemSkus = computed(() => {
  const results = []
  props.itemSkuContents.forEach(itemSku => {
    const alreadyPushedItem = results.find(
      result => result.itemSkuId === itemSku.itemSkuId
    )
    // 既に存在する sku の場合は数量を加算する
    if (alreadyPushedItem) {
      alreadyPushedItem.quantity += itemSku.totalQuantity
      return
    }
    // 存在しない場合は新たに追加する
    results.push({
      brandName: itemSku.brandName,
      itemName: itemSku.itemName,
      itemSkuId: itemSku.itemSkuId,
      itemSkuName: itemSku.hasMultipleItemSkus ? itemSku.itemSkuName : '',
      price: itemSku.price,
      quantity: itemSku.totalQuantity,
      ticketType: itemSku.ticketType,
      isIncludingTax: itemSku.isIncludingTax,
      limitedEndDateTime: itemSku.limitedEndDateTime
        ? new Date(itemSku.limitedEndDateTime)
        : null
    })
  })

  return results
})

const isSummaryPriceIncludingTax = computed(() => {
  return itemSkus.value.some(itemSku => itemSku.isIncludingTax)
})

const onSubmit = () => {
  // session に保存されている情報を元に作成するため、フォームに値は存在しない
  // cf. Pickup::BulkOrdersController#create
  isSubmitting.value = true
}
</script>

<style lang="scss" scoped>
.bulk-order-confirm {
  &__header {
    &-container {
      display: flex;
      background-color: $white;
      justify-content: center;
    }

    &-title {
      max-width: $base-contents-width;
      width: 100%;
    }
  }

  &__contents-container {
    background-color: $white;
  }

  &__contents {
    max-width: $base-contents-width;
    width: 100%;
    margin: auto;
  }

  &__gift {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__action-container {
    background-color: $white;
  }

  &__action {
    max-width: $base-contents-width;
    margin: auto;
    padding: 0 16px;
    box-sizing: border-box;
  }

  &__button-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__submit-button {
    max-width: 343px;
  }

  &__caption {
    max-width: $base-contents-width;
    width: 100%;
    text-align: left;
  }

  &__link-text {
    font-size: 1rem;
    line-height: 1.6;
  }
}
</style>
